// src/utils/promptCoachParser.ts

import { v4 as uuidv4 } from "uuid";
import {
  ParseResult,
  PromptCoachData,
  ParseError,
  ClickablePrompt,
  PromptCoachElement,
  ClickablePromptsElement,
} from "../interfaces/promptCoachTypes";
import { createLogger } from "./logger";

const log = createLogger("promptCoachParser");

/**
 * Prompt Coach Parser
 *
 * Parses Prompt Coach sections from AI-generated markdown responses.
 * All agents use the same XML-tagged format for consistency.
 *
 * Design Philosophy:
 * - Never throw exceptions - always return valid ParseResult
 * - Graceful degradation - partial data better than complete failure
 * - Defensive parsing - handle malformed input safely
 * - Comprehensive logging - log complete section content when parsing fails
 * - Agent-agnostic - works the same for all agents (no agent-specific logic)
 */

/**
 * Extract content between XML tags
 * Returns null if tag not found
 */
function extractXmlTag(content: string, tagName: string): string | null {
  const openTag = `<${tagName}>`;
  const closeTag = `</${tagName}>`;

  const startIndex = content.indexOf(openTag);
  if (startIndex === -1) return null;

  const contentStart = startIndex + openTag.length;
  const endIndex = content.indexOf(closeTag, contentStart);
  if (endIndex === -1) return null;

  return content.substring(contentStart, endIndex).trim();
}

/**
 * Extract numbered list items from NEXT_STEPS XML content
 * Returns empty array if no items found
 *
 * Handles multi-line items correctly by splitting on numbered list markers
 * and capturing all content until the next numbered item.
 */
function extractNextStepsItems(content: string): ClickablePrompt[] {
  const items: ClickablePrompt[] = [];

  // Split content by numbered list markers: "1. " or "1) "
  // This preserves multi-line content within each item
  const parts = content.split(/(?=^\s*\d+[.)]\s+)/gm);

  for (const part of parts) {
    // Extract the numbered item: "1. text..." or "1) text..."
    // Use [\s\S] to match across newlines (ES5 compatible)
    const match = part.match(/^\s*\d+[.)]\s+([\s\S]+)/);

    if (match) {
      const text = cleanContent(match[1]);
      if (text.length > 0) {
        items.push({
          id: uuidv4(),
          text,
        });
      }
    }
  }

  return items;
}

/**
 * Parse Prompt Coach using XML tags (primary method)
 * Returns partial data - any tags found will be included
 */
function parseXmlFormat(content: string): PromptCoachData | null {
  const data: PromptCoachData = {
    rawSectionContent: content,
  };

  let foundAnyContent = false;

  // Extract Strategic Question (Element 1)
  const strategicQuestion = extractXmlTag(content, "STRATEGIC_QUESTION");
  if (strategicQuestion) {
    data.element1 = {
      label: "Strategic Question",
      content: cleanContent(strategicQuestion),
    };
    foundAnyContent = true;
  }

  // Extract Prompt Enhancement (Element 2)
  const promptEnhancement = extractXmlTag(content, "PROMPT_ENHANCEMENT");
  if (promptEnhancement) {
    data.element2 = {
      label: "Prompt Enhancement",
      content: cleanContent(promptEnhancement),
    };
    foundAnyContent = true;
  }

  // Extract Next Steps (Element 3)
  const nextSteps = extractXmlTag(content, "NEXT_STEPS");
  if (nextSteps) {
    const clickablePrompts = extractNextStepsItems(nextSteps);
    if (clickablePrompts.length > 0) {
      data.element3 = {
        label: "Next Steps",
        clickablePrompts,
      };
      foundAnyContent = true;
    }
  }

  // Return data if we found at least one element, otherwise null
  return foundAnyContent ? data : null;
}

/**
 * Parse Prompt Coach section from markdown content
 *
 * @param markdownContent - Full markdown string to parse
 * @param isStreaming - Whether content is being streamed (suppresses expected errors)
 * @returns ParseResult with success state, data, and errors
 */
export function parsePromptCoach(
  markdownContent: string,
  isStreaming: boolean = false
): ParseResult {
  const errors: ParseError[] = [];

  // Edge Case 1: Empty or null input
  if (!markdownContent || markdownContent.trim() === "") {
    return {
      success: false,
      originalMarkdown: markdownContent || "",
      errors: [
        {
          code: "MALFORMED_MARKDOWN",
          message: "Empty markdown content provided",
          severity: "error",
        },
      ],
    };
  }

  // Find Prompt Coach heading (H1-H4 or plain text, case-insensitive)
  // Matches: "## Prompt Coach" (preferred), "## **Prompt Coach**" (bold), "## *Prompt Coach*" (italic)
  // Made flexible to handle cases where LLM doesn't output heading markers or uses markdown formatting
  const headingRegex = /^(#{1,4}\s+)?[\*_]*Prompt Coach[\*_]*\s*$/im;
  const headingMatch = markdownContent.match(headingRegex);

  // Edge Case 2: Heading not found
  // Try parsing content directly as XML (may be pre-extracted by extractPromptCoach's XML fallback)
  if (!headingMatch || headingMatch.index === undefined) {
    // Attempt to parse as XML format (content may already be extracted)
    const data = parseXmlFormat(markdownContent.trim());

    if (data) {
      // Successfully parsed as XML - return success
      // This handles the case where extractPromptCoach used XML fallback and returned heading-less content
      return {
        success: true,
        data,
        originalMarkdown: markdownContent,
        errors: undefined,
      };
    }

    // Not XML either - truly no Prompt Coach content found
    return {
      success: false,
      originalMarkdown: markdownContent,
      errors: [
        {
          code: "HEADING_NOT_FOUND",
          message: "No Prompt Coach heading found in markdown",
          severity: "error",
        },
      ],
    };
  }

  // Extract section content (from heading to next heading or EOF)
  const startIndex = headingMatch.index + headingMatch[0].length;
  const restOfContent = markdownContent.substring(startIndex);
  const nextHeadingMatch = restOfContent.match(/^#{1,4}\s+/m);
  const sectionContent = nextHeadingMatch
    ? restOfContent.substring(0, nextHeadingMatch.index)
    : restOfContent;

  // Edge Case 3: Empty section content
  if (!sectionContent.trim()) {
    return {
      success: false,
      originalMarkdown: markdownContent,
      errors: [
        {
          code: "MALFORMED_MARKDOWN",
          message: "Prompt Coach section is empty",
          severity: "error",
          context: "Section found but contains no content",
        },
      ],
    };
  }

  // Parse using XML format ONLY
  try {
    let data: PromptCoachData | null = null;

    // Parse XML format (only method now - all agents use same format)
    data = parseXmlFormat(sectionContent);

    if (!data) {
      // COMPREHENSIVE LOGGING: Log the complete Prompt Coach section when parsing fails
      // Only log ERROR if NOT streaming (expected to fail during streaming with incomplete XML)
      if (!isStreaming) {
        log.error("XML parsing failed - logging complete Prompt Coach section", {
          fullSection: sectionContent,
          sectionLength: sectionContent.length,
          hasOpenTags: {
            strategicQuestion: sectionContent.includes("<STRATEGIC_QUESTION>"),
            promptEnhancement: sectionContent.includes("<PROMPT_ENHANCEMENT>"),
            nextSteps: sectionContent.includes("<NEXT_STEPS>"),
          },
          hasCloseTags: {
            strategicQuestion: sectionContent.includes("</STRATEGIC_QUESTION>"),
            promptEnhancement: sectionContent.includes("</PROMPT_ENHANCEMENT>"),
            nextSteps: sectionContent.includes("</NEXT_STEPS>"),
          },
        });
      }

      return {
        success: false,
        originalMarkdown: markdownContent,
        errors: [
          {
            code: "FORMAT_MISMATCH",
            message: "Failed to parse Prompt Coach section - XML tags not found or incomplete",
            severity: "error",
            context: isStreaming
              ? "Content incomplete during streaming"
              : "See logs for complete section content",
          },
        ],
      };
    }

    // Add raw content
    data.rawSectionContent = sectionContent;

    // Edge Case 4: Check if we have enough content for UI rendering
    const hasElement1 = data.element1 && data.element1.content.trim().length > 0;
    const hasElement2 = data.element2 && data.element2.content.trim().length > 0;
    const hasElement3 = data.element3 && data.element3.clickablePrompts.length > 0;

    const elementCount = [hasElement1, hasElement2, hasElement3].filter(Boolean).length;

    // Edge Case 5: Partial section (some elements missing)
    if (elementCount === 0) {
      // COMPREHENSIVE LOGGING: Parsing succeeded but found no valid elements
      // Only log ERROR if NOT streaming (expected during streaming with incomplete content)
      if (!isStreaming) {
        log.error("XML parsing succeeded but found no valid elements", {
          fullSection: sectionContent,
          sectionLength: sectionContent.length,
        });
      }

      return {
        success: false,
        originalMarkdown: markdownContent,
        errors: [
          {
            code: "ELEMENT_MISSING",
            message: "No valid elements found in Prompt Coach section",
            severity: "error",
          },
        ],
      };
    }

    // Partial success - log warnings for missing elements
    if (!hasElement1) {
      errors.push({
        code: "ELEMENT_MISSING",
        message: "Element 1 (Strategic Question) not found",
        severity: "warning",
      });
    }
    if (!hasElement2) {
      errors.push({
        code: "ELEMENT_MISSING",
        message: "Element 2 (Prompt Enhancement) not found",
        severity: "warning",
      });
    }
    if (!hasElement3) {
      errors.push({
        code: "ELEMENT_MISSING",
        message: "Element 3 (Next Steps) not found or empty",
        severity: "warning",
      });
    }

    return {
      success: true,
      data,
      originalMarkdown: markdownContent,
      errors: errors.length > 0 ? errors : undefined,
    };
  } catch (error) {
    // Defensive: Catch any unexpected errors + log complete section
    log.error("Unexpected parsing error", {
      error: error instanceof Error ? error.message : "Unknown error",
      stack: error instanceof Error ? error.stack : undefined,
      fullSection: sectionContent,
    });

    return {
      success: false,
      originalMarkdown: markdownContent,
      errors: [
        {
          code: "MALFORMED_MARKDOWN",
          message: `Unexpected parsing error: ${error instanceof Error ? error.message : "Unknown error"}`,
          severity: "error",
        },
      ],
    };
  }
}

/**
 * Clean and normalize content
 * - Trim whitespace
 * - Remove extra newlines
 * - Remove markdown formatting artifacts
 */
function cleanContent(content: string): string {
  return content
    .trim()
    .replace(/\n+/g, " ") // Replace newlines with spaces
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim();
}
